import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useHelpHub } from '../context/HelpHubContext';
import {
LayoutGrid,
  CreditCard,
  User,
  Building2,
  FileText,
  HelpCircle,
  LogOut,
  ChevronLeft,
  ChevronRight,
  ChevronsUpDown,
  Check,
  Plus,
} from 'lucide-react';

interface SidebarProps {
  collapsed: boolean;
  onToggle: () => void;
  width: number;
}

// Brandable logo URLs - can be replaced for white-labeling
const DEFAULT_LOGO_FULL = '/assets/dashboardfox-logo.png';
const DEFAULT_LOGO_ICON = '/assets/dashboardfox-symbol.png';

const Sidebar: React.FC<SidebarProps> = ({ collapsed, onToggle, width }) => {
  const navigate = useNavigate();
  const location = useLocation();
const { user, agencyRole, logout, branding, agencies, agencyId, agencyName, switchAgency } = useAuth();
  const logoFull = branding?.logo_url || DEFAULT_LOGO_FULL;
  const logoIcon = branding?.logo_small_url || DEFAULT_LOGO_ICON;
  const [hoveredItem, setHoveredItem] = useState<string | null>(null);
  const [logoutHovered, setLogoutHovered] = useState(false);
const [toggleHovered, setToggleHovered] = useState(false);
  const [agencySwitcherOpen, setAgencySwitcherOpen] = useState(false);
  const { open: openHelpHub } = useHelpHub();
  const [supportHovered, setSupportHovered] = useState(false);


  const canAccessBilling = agencyRole === 'owner' || agencyRole === 'billing_admin';
  const isOwner = agencyRole === 'owner';

  // Navigation items with Lucide icons
  const mainNavItems = [
    { id: 'workspaces', label: 'Workspaces', icon: LayoutGrid, path: '/dashboard' },
    { id: 'billing', label: 'Billing', icon: CreditCard, path: '/dashboard/billing', show: canAccessBilling },
  ].filter(item => item.show !== false);

  const settingsNavItems = [
    { id: 'profile', label: 'Profile', icon: User, path: '/dashboard/profile' },
{ id: 'agency', label: 'Agency', icon: Building2, path: '/dashboard/agency', show: isOwner || agencyRole === 'member' },
{ id: 'audit-logs', label: 'Audit Logs', icon: FileText, path: '/dashboard/audit-logs', show: isOwner },
  ].filter(item => item.show !== false);

  const isActive = (path: string) => {
    if (path === '/dashboard') {
      return location.pathname === '/dashboard' || location.pathname.startsWith('/dashboard/workspace/');
    }
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };

  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  // ============================================
  // STYLES - Better Stack Inspired
  // ============================================

  const sidebarStyle: React.CSSProperties = {
    width,
    position: 'fixed',
    left: 0,
    top: 0,
    height: '100vh',
    backgroundColor: 'var(--v2-surface)',
    borderRight: '1px solid var(--v2-border)',
    display: 'flex',
    flexDirection: 'column',
    transition: 'width 0.2s ease',
    zIndex: 1200,
    fontFamily: 'var(--font-sans)',
  };

  const logoSectionStyle: React.CSSProperties = {
    padding: collapsed ? '16px 12px' : '16px 16px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: collapsed ? 'center' : 'space-between',
    borderBottom: '1px solid var(--v2-border)',
    minHeight: '64px',
    gap: '12px',
  };

  const logoContainerStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    flexShrink: collapsed ? 0 : 1,
    minWidth: 0,
    overflow: 'hidden',
  };

  const toggleButtonStyle: React.CSSProperties = {
    background: toggleHovered ? 'var(--v2-surface-hover)' : 'transparent',
    border: 'none',
    cursor: 'pointer',
    padding: '6px',
    borderRadius: 'var(--radius-sm)',
    color: toggleHovered ? 'var(--v2-text-secondary)' : 'var(--v2-text-muted)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'all 0.15s ease',
    flexShrink: 0,
  };

  const navSectionStyle: React.CSSProperties = {
    padding: collapsed ? '12px 8px' : '12px',
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    overflowY: 'auto',
  };

  const sectionHeaderStyle: React.CSSProperties = {
    fontSize: '0.6875rem',
    fontWeight: 500,
    color: 'var(--v2-text-muted)',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    padding: collapsed ? '12px 0 8px' : '12px 12px 8px',
    marginTop: '8px',
  };

const getSupportButtonStyle = (hovered: boolean): React.CSSProperties => ({
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
  padding: collapsed ? '10px' : '10px 12px',
  borderRadius: 'var(--radius-md)',
  cursor: 'pointer',
  transition: 'all 0.15s ease',
  backgroundColor: hovered ? 'var(--v2-primary-light)' : 'var(--v2-surface-alt)',
  color: hovered ? 'var(--v2-primary)' : 'var(--v2-text-secondary)',
  fontWeight: 500,
  fontSize: '0.875rem',
  justifyContent: collapsed ? 'center' : 'flex-start',
  border: 'none',
  width: '100%',
  textAlign: 'left',
});

  const getNavItemStyle = (active: boolean, hovered: boolean): React.CSSProperties => ({
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: collapsed ? '10px' : '10px 12px',
    borderRadius: 'var(--radius-md)',
    cursor: 'pointer',
    transition: 'all 0.15s ease',
    backgroundColor: active 
      ? 'var(--v2-primary-light)' 
      : hovered 
        ? 'var(--v2-surface-hover)' 
        : 'transparent',
    color: active ? 'var(--v2-primary)' : 'var(--v2-text-secondary)',
    fontWeight: active ? 500 : 400,
    fontSize: '0.875rem',
    justifyContent: collapsed ? 'center' : 'flex-start',
    marginBottom: '2px',
    border: 'none',
    width: '100%',
    textAlign: 'left',
  });

  const userSectionStyle: React.CSSProperties = {
    padding: collapsed ? '12px 8px' : '12px',
    borderTop: '1px solid var(--v2-border)',
  };

  const userInfoStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: collapsed ? '8px' : '10px 12px',
    borderRadius: 'var(--radius-md)',
    backgroundColor: 'var(--v2-surface-alt)',
    justifyContent: collapsed ? 'center' : 'flex-start',
  };

  const avatarStyle: React.CSSProperties = {
    width: '34px',
    height: '34px',
    borderRadius: '50%',
    backgroundColor: 'var(--v2-primary)',
    color: '#ffffff',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '0.8125rem',
    fontWeight: 600,
    flexShrink: 0,
  };

  const logoutButtonStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: collapsed ? 'center' : 'flex-start',
    gap: '12px',
    width: '100%',
    padding: collapsed ? '10px' : '10px 12px',
    marginTop: '8px',
    border: 'none',
    borderRadius: 'var(--radius-md)',
    backgroundColor: logoutHovered ? 'var(--v2-error-bg)' : 'transparent',
    color: 'var(--v2-error)',
    fontSize: '0.875rem',
    fontWeight: 400,
    cursor: 'pointer',
    transition: 'background-color 0.15s ease',
  };

  return (
    <div style={sidebarStyle}>
      {/* Logo Header */}
      <div style={logoSectionStyle}>
        <div style={logoContainerStyle} onClick={() => navigate('/dashboard')}>
          {collapsed ? (
<img
              src={logoIcon}
              alt={branding?.company_name || 'DashboardFox'}
              style={{ height: '36px', width: '36px', objectFit: 'contain' }}
            />          ) : (
<img
              src={logoFull}
              alt="DashboardFox"
              style={{ height: '48px', maxHeight: '48px', width: 'auto', maxWidth: '180px', objectFit: 'contain' }}
            />          )}
        </div>
        <button
          onClick={onToggle}
          onMouseEnter={() => setToggleHovered(true)}
          onMouseLeave={() => setToggleHovered(false)}
          style={toggleButtonStyle}
          title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        >
          {collapsed ? <ChevronRight size={18} strokeWidth={1.5} /> : <ChevronLeft size={18} strokeWidth={1.5} />}
        </button>
      </div>

      {/* Navigation */}
      <div style={navSectionStyle}>
        {/* Main Nav */}
        <div>
          {mainNavItems.map((item) => {
            const Icon = item.icon;
            const active = isActive(item.path);
            const hovered = hoveredItem === item.id;
            return (
              <button
                key={item.id}
                onClick={() => navigate(item.path)}
                onMouseEnter={() => setHoveredItem(item.id)}
                onMouseLeave={() => setHoveredItem(null)}
                style={getNavItemStyle(active, hovered)}
                title={collapsed ? item.label : undefined}
              >
                <Icon 
                  size={20} 
                  strokeWidth={active ? 2 : 1.5} 
                  style={{ flexShrink: 0 }}
                />
                {!collapsed && <span>{item.label}</span>}
              </button>
            );
          })}
        </div>

        {/* Settings Section */}
        <div>
          {!collapsed && <div style={sectionHeaderStyle}>Settings</div>}
          {collapsed && <div style={{ height: '20px' }} />}
          {settingsNavItems.map((item) => {
            const Icon = item.icon;
            const active = isActive(item.path);
            const hovered = hoveredItem === item.id;
            return (
              <button
                key={item.id}
                onClick={() => navigate(item.path)}
                onMouseEnter={() => setHoveredItem(item.id)}
                onMouseLeave={() => setHoveredItem(null)}
                style={getNavItemStyle(active, hovered)}
                title={collapsed ? item.label : undefined}
              >
                <Icon 
                  size={20} 
                  strokeWidth={active ? 2 : 1.5}
                  style={{ flexShrink: 0 }}
                />
{!collapsed && <span>{item.label}</span>}
              </button>
            );
          })}
</div>
      </div>

      {/* Support Section */}
      <div style={{
        padding: collapsed ? '12px 8px' : '12px',
        borderTop: '1px solid var(--v2-border)',
      }}>
        <button
          onClick={openHelpHub}
          onMouseEnter={() => setSupportHovered(true)}
          onMouseLeave={() => setSupportHovered(false)}
          style={getSupportButtonStyle(supportHovered)}
          title={collapsed ? 'Support' : undefined}
        >
          <HelpCircle size={18} strokeWidth={1.5} style={{ flexShrink: 0, color: 'var(--v2-primary)' }} />
          {!collapsed && <span>Support</span>}
        </button>
      </div>

{/* Agency Switcher - only show if multiple agencies */}
{(agencies.length > 0) && (
        <div style={{ padding: collapsed ? '8px' : '8px 12px', borderTop: '1px solid var(--v2-border)', position: 'relative' }}>
          <button
            onClick={() => setAgencySwitcherOpen(!agencySwitcherOpen)}
            style={{
              display: 'flex', alignItems: 'center', gap: '8px',
              width: '100%', padding: collapsed ? '8px' : '8px 10px',
              backgroundColor: agencySwitcherOpen ? 'var(--v2-surface-hover)' : 'transparent',
              border: '1px solid var(--v2-border)', borderRadius: 'var(--radius-md)',
              cursor: 'pointer', justifyContent: collapsed ? 'center' : 'space-between',
            }}
            title={collapsed ? agencyName || 'Switch Agency' : undefined}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', minWidth: 0 }}>
              <Building2 size={16} strokeWidth={1.5} color="var(--v2-text-muted)" style={{ flexShrink: 0 }} />
              {!collapsed && (
                <span style={{
                  fontSize: '0.8125rem', fontWeight: 500, color: 'var(--v2-text)',
                  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'
                }}>
                  {agencyName || 'Agency'}
                </span>
              )}
            </div>
            {!collapsed && <ChevronsUpDown size={14} strokeWidth={1.5} color="var(--v2-text-muted)" />}
          </button>
          {agencySwitcherOpen && (
            <div style={{
              position: 'absolute', bottom: '100%', left: collapsed ? '100%' : '12px',
              right: collapsed ? 'auto' : '12px',
              marginBottom: '4px', backgroundColor: 'var(--v2-surface)',
              border: '1px solid var(--v2-border)', borderRadius: 'var(--radius-md)',
              boxShadow: 'var(--shadow-lg)', zIndex: 50, overflow: 'hidden', padding: '4px',
              minWidth: collapsed ? '200px' : 'auto',
            }}>
              {agencies.map(a => (
                <button
                  key={a.agency_id}
                  onClick={async () => {
                    setAgencySwitcherOpen(false);
                    await switchAgency(a.agency_id);
                    navigate('/dashboard');
                  }}
                  style={{
                    display: 'flex', alignItems: 'center', gap: '8px',
                    width: '100%', padding: '8px 10px', border: 'none',
                    backgroundColor: a.agency_id === agencyId ? 'var(--v2-primary-light)' : 'transparent',
                    borderRadius: 'var(--radius-sm)', cursor: 'pointer', textAlign: 'left',
                  }}
                  onMouseOver={(e) => {
                    if (a.agency_id !== agencyId) e.currentTarget.style.backgroundColor = 'var(--v2-surface-hover)';
                  }}
                  onMouseOut={(e) => {
                    if (a.agency_id !== agencyId) e.currentTarget.style.backgroundColor = 'transparent';
                  }}
                >
                  <Building2 size={14} strokeWidth={1.5} color={a.agency_id === agencyId ? 'var(--v2-primary)' : 'var(--v2-text-muted)'} />
                  <span style={{
                    fontSize: '0.875rem', flex: 1,
                    color: a.agency_id === agencyId ? 'var(--v2-primary)' : 'var(--v2-text)',
                    fontWeight: a.agency_id === agencyId ? 600 : 400,
                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'
                  }}>
                    {a.name}
                  </span>
                  {a.agency_id === agencyId && <Check size={14} strokeWidth={2} color="var(--v2-primary)" />}
                </button>
              ))}
{!agencies.some(a => a.role === 'owner') && (
  <>
    <div style={{ height: '1px', backgroundColor: 'var(--v2-border)', margin: '4px 0' }} />
    <button
      onClick={() => {
        setAgencySwitcherOpen(false);
        navigate('/dashboard/create-agency');
      }}
      style={{
        display: 'flex', alignItems: 'center', gap: '8px',
        width: '100%', padding: '8px 10px', border: 'none',
        backgroundColor: 'transparent',
        borderRadius: 'var(--radius-sm)', cursor: 'pointer', textAlign: 'left',
      }}
      onMouseOver={(e) => { e.currentTarget.style.backgroundColor = 'var(--v2-surface-hover)'; }}
      onMouseOut={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
    >
      <Plus size={14} strokeWidth={1.5} color="var(--v2-text-muted)" />
      <span style={{ fontSize: '0.875rem', color: 'var(--v2-text)', fontWeight: 400 }}>
        Create New Agency
      </span>
    </button>
  </>
)}
            </div>
          )}
        </div>
      )}

      {/* User Section */}
      <div style={userSectionStyle}>
        <div style={userInfoStyle}>
          <div style={avatarStyle}>
            {user?.name ? getInitials(user.name) : '?'}
          </div>
          {!collapsed && (
            <div style={{ overflow: 'hidden', flex: 1 }}>
              <div style={{
                fontSize: '0.875rem',
                fontWeight: 500,
                color: 'var(--v2-text)',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}>
                {user?.name || 'User'}
              </div>
              <div style={{
                fontSize: '0.75rem',
                color: 'var(--v2-text-muted)',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                marginTop: '1px',
              }}>
                {user?.email}
              </div>
            </div>
          )}
</div>
        
        
        <button
          onClick={logout}
          onMouseEnter={() => setLogoutHovered(true)}
          onMouseLeave={() => setLogoutHovered(false)}
          style={logoutButtonStyle}
          title={collapsed ? 'Logout' : undefined}
        >
          <LogOut size={18} strokeWidth={1.5} />
          {!collapsed && <span>Logout</span>}
        </button>
      </div>
    </div>
  );
};

export default Sidebar;
